import { useCallback, useEffect, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
  $createTextNode
} from "lexical";
import AtMentionOverlay from "@/components/AtMention/AtMentionOverlay";
import { $createMentionNode } from "@/lib/lexical/nodes/MentionNode";
import type { MentionOverlayCopy, MentionUser } from "@/components/AtMention/types";

const MENTION_TRIGGER_REGEX = /(^|\s)@(\S*)$/;

interface MentionPluginProps {
  users: MentionUser[];
  isLoading?: boolean;
  onQueryChange: (query: string) => void;
  consumeEnterWhenOpen?: boolean;
  overlayClassName?: string;
  overlay?: MentionOverlayCopy;
  resolveSelect?: (user: MentionUser) => boolean;
  dismiss?: () => boolean;
}

export default function MentionPlugin({
  users,
  isLoading = false,
  onQueryChange,
  consumeEnterWhenOpen = false,
  overlayClassName,
  overlay,
  resolveSelect,
  dismiss
}: MentionPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  const closeOverlay = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setSelectedIndex(0);
    onQueryChange("");
  }, [onQueryChange]);

  const insertMention = useCallback(
    (user: MentionUser) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();
        if (!$isTextNode(anchorNode)) return;

        const textContent = anchorNode.getTextContent();
        const anchorOffset = anchor.offset;
        const textUpToCursor = textContent.slice(0, anchorOffset);
        const match = textUpToCursor.match(MENTION_TRIGGER_REGEX);
        if (!match) return;

        const matchStart = match.index! + match[1].length;
        const matchEnd = anchorOffset;

        const mentionNode = $createMentionNode(user);
        const trailingSpace = $createTextNode(" ");

        if (matchStart === 0 && matchEnd === textContent.length) {
          anchorNode.replace(mentionNode);
          mentionNode.insertAfter(trailingSpace);
        } else if (matchStart === 0) {
          const [targetNode, remainingNode] = anchorNode.splitText(matchEnd);
          targetNode.replace(mentionNode);
          mentionNode.insertAfter(trailingSpace);
          if (remainingNode) {
            trailingSpace.insertAfter(remainingNode);
          }
        } else {
          const [, targetNode] = anchorNode.splitText(matchStart);
          if (!targetNode) return;
          if (matchEnd < textContent.length) {
            const [toReplace] = targetNode.splitText(matchEnd - matchStart);
            toReplace.replace(mentionNode);
          } else {
            targetNode.replace(mentionNode);
          }
          mentionNode.insertAfter(trailingSpace);
        }

        trailingSpace.select();
      });

      closeOverlay();
    },
    [editor, closeOverlay]
  );

  const selectOption = useCallback(
    (user: MentionUser) => {
      if (resolveSelect?.(user) === false) {
        setSelectedIndex(user.selectAction === "expand" ? 1 : 0);
        return;
      }
      insertMention(user);
    },
    [insertMention, resolveSelect]
  );

  useEffect(() => {
    const removeListener = editor.registerTextContentListener(() => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          if (isOpen) closeOverlay();
          return;
        }

        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();
        if (!$isTextNode(anchorNode)) {
          if (isOpen) closeOverlay();
          return;
        }

        const textContent = anchorNode.getTextContent();
        const textUpToCursor = textContent.slice(0, anchor.offset);
        const match = textUpToCursor.match(MENTION_TRIGGER_REGEX);

        if (match) {
          const mentionQuery = match[2];
          setIsOpen(true);
          setQuery(mentionQuery);
          setSelectedIndex(0);
          onQueryChange(mentionQuery);
        } else if (isOpen) {
          closeOverlay();
        }
      });
    });

    return removeListener;
  }, [editor, isOpen, closeOverlay, onQueryChange]);

  useEffect(() => {
    if (!isOpen) return;

    const commands = [
      editor.registerCommand(
        KEY_ARROW_DOWN_COMMAND,
        (event) => {
          event.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % Math.max(users.length, 1));
          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        KEY_ARROW_UP_COMMAND,
        (event) => {
          event.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + Math.max(users.length, 1)) % Math.max(users.length, 1));
          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (users.length > 0 && selectedIndex >= 0 && selectedIndex < users.length) {
            event?.preventDefault();
            selectOption(users[selectedIndex]);
            return true;
          }
          if (consumeEnterWhenOpen) {
            event?.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        KEY_TAB_COMMAND,
        (event) => {
          if (users.length > 0 && selectedIndex >= 0 && selectedIndex < users.length) {
            event.preventDefault();
            selectOption(users[selectedIndex]);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        (event) => {
          event.preventDefault();
          if (dismiss?.() === false) {
            setSelectedIndex(0);
            return true;
          }
          closeOverlay();
          return true;
        },
        COMMAND_PRIORITY_HIGH
      )
    ];

    return () => commands.forEach((cleanup) => cleanup());
  }, [editor, isOpen, users, selectedIndex, selectOption, closeOverlay, consumeEnterWhenOpen, dismiss]);

  if (!isOpen) return null;

  return (
    <div className="absolute left-0 right-0 z-50" data-testid="mention-plugin-container">
      <AtMentionOverlay
        users={users}
        isLoading={isLoading}
        query={query}
        selectedIndex={selectedIndex}
        onSelect={selectOption}
        onHighlightChange={setSelectedIndex}
        className={overlayClassName}
        overlay={overlay}
      />
    </div>
  );
}
